import { CustomSelectItem } from '@/components/CustomSelect';
import { CustomAutoComplete } from '@/components/ui/Autocomplete';
import { Input } from '@/components/ui/Input';
import { billAmountContent } from '@/content/billAmountContent';
import { useStateDropdown } from '@/routes/hooks/useStateDropdown';
import { BillAmountProvider, StateDropdownItem } from '@/state/state.types';
import { Flex, Grid, Modal, useMantineTheme } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useEffect } from 'react';
import { z } from 'zod';
import { PrimaryButton } from './PrimaryButton';

const customProviderSchema = z.object({
  name: z.string().nonempty('Please enter a hospital name'),
  city: z.string().nonempty('Please enter a city'),
  state: z.string().nonempty('Please select a state'),
});

type CustomProviderFormValues = z.infer<typeof customProviderSchema>;

type CustomProviderModalProps = {
  opened: boolean;
  onClose: () => void;
  onSave: (provider: BillAmountProvider) => void;
  initialValues?: BillAmountProvider;
};

export const CustomProviderModal = ({
  opened,
  onClose,
  onSave,
  initialValues,
}: CustomProviderModalProps) => {
  const { stateDropdown_: stateDropdownData, isLoading: stateDataLoading } = useStateDropdown();
  const theme = useMantineTheme();

  const form = useForm<CustomProviderFormValues>({
    validate: zodResolver(customProviderSchema),
    validateInputOnBlur: true,
    initialValues: {
      name: initialValues?.name ?? '',
      city: initialValues?.city ?? '',
      state: initialValues?.state ?? '',
    },
  });

  useEffect(() => {
    if (opened) {
      form.setValues({
        name: initialValues?.name ?? '',
        city: initialValues?.city ?? '',
        state: initialValues?.state ?? '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, initialValues]);

  const handleSave = () => {
    const validation = form.validate();
    if (validation.hasErrors) return;

    const values = customProviderSchema.parse(form.values);
    onSave({
      name: values.name,
      city: values.city,
      state: values.state,
    });
  };

  const handleCancel = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal.Root
      opened={opened}
      onClose={handleCancel}
      size="auto"
      centered
    >
      <Modal.Overlay
        color={theme.colors.primary[0]}
        opacity={0.85}
      />
      <Modal.Content
        radius={theme.radius.xl}
        p="lg"
        sx={{ overflow: 'visible' }}
      >
        <Modal.Header
          style={{
            display: 'flex',
            justifyContent: 'center',
            paddingBottom: theme.spacing.md,
            zIndex: 'auto',
          }}
        >
          <Modal.Title
            style={{
              fontSize: theme.fontSizes.xl,
              fontWeight: 700,
              color: theme.colors.primary[0],
            }}
          >
            {billAmountContent.modalTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body sx={{ overflow: 'visible' }}>
          <Grid gutter="md">
            <Grid.Col>
              <Input
                type="text"
                name="name"
                inputProps={form.getInputProps('name')}
                error={form.errors.name}
                placeholder="Enter hospital name"
                label={billAmountContent.modalHospitalLabel}
                isLoading={false}
              />
            </Grid.Col>
            <Grid.Col
              span={12}
              md={8}
            >
              <Input
                type="text"
                name="city"
                inputProps={form.getInputProps('city')}
                error={form.errors.city}
                placeholder="Enter city"
                label={billAmountContent.modalCityLabel}
                isLoading={false}
              />
            </Grid.Col>
            <Grid.Col
              span={12}
              md={4}
            >
              <CustomAutoComplete
                label={billAmountContent.modalStateLabel}
                data={(stateDropdownData ?? []) as StateDropdownItem[]}
                placeholder="State"
                searchable
                dropdownComponent="div"
                dropdownPosition="flip"
                itemComponent={CustomSelectItem}
                error={form.errors.state}
                isLoading={stateDataLoading}
                withinPortal
                {...form.getInputProps('state')}
              />
            </Grid.Col>
          </Grid>
          <Flex
            gap="md"
            mt={24}
            justify="center"
          >
            <PrimaryButton
              type="button"
              onClick={handleCancel}
              isOutline
            >
              {billAmountContent.modalCancelLabel}
            </PrimaryButton>
            <PrimaryButton
              type="button"
              onClick={handleSave}
              isDisabled={!form.isValid()}
            >
              {billAmountContent.modalSaveLabel}
            </PrimaryButton>
          </Flex>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
